import { logger } from '../utils/logger';
/**
 * Centralized environment configuration
 * All environment variables should be accessed through this file
 */

// Helper to get env var with validation
function getEnvVar(key: string, fallback?: string): string {
  const value = import.meta.env[key];
  
  if (!value) {
    if (fallback !== undefined) {
      logger.warn(`[ENV] ${key} not set, using fallback: ${fallback}`);
      return fallback;
    }
    throw new Error(`[ENV] Required environment variable ${key} is not set`);
  }
  
  return value;
}

// Helper to get optional env var
function getOptionalEnvVar(key: string, fallback: string): string {
  return import.meta.env[key] || fallback;
}

// Helper to get boolean env var
function getBooleanEnv(key: string, fallback: boolean = false): boolean {
  const value = import.meta.env[key];
  if (!value) return fallback;
  return value === 'true' || value === '1';
}

/**
 * Keycloak Configuration
 */
export const KEYCLOAK_CONFIG = {
  url: getEnvVar('VITE_KEYCLOAK_URL'),
  realm: getEnvVar('VITE_KEYCLOAK_REALM'),
  clientId: getEnvVar('VITE_KEYCLOAK_CLIENT_ID'),
  adminUrl: getEnvVar('VITE_KEYCLOAK_ADMIN_URL'),
  debug: getBooleanEnv('VITE_KEYCLOAK_DEBUG', import.meta.env.DEV)
} as const;

/**
 * API Configuration
 */
export const API_CONFIG = {
  baseUrl: getEnvVar('VITE_API_BASE_URL')
} as const;

/**
 * Frontend URLs
 */
export const APP_URLS = {
  main: getEnvVar('VITE_APP_URL'),
  practice: getEnvVar('VITE_PRACTICE_SITE_URL')
} as const;

/**
 * External APIs
 * Bootstrap fallbacks only: the live media hosts come from GET /api/public/media-hosts.
 */
export const EXTERNAL_APIS = {
  tts: getOptionalEnvVar('VITE_TTS_API_URL', 'https://m4m-tts.gioitienganh.vn'),
  asrWsHost: getOptionalEnvVar('VITE_ASR_WS_HOST', 'm4m-asr.gioitienganh.vn')
} as const;

/**
 * Feature Flags
 */
export const FEATURES = {
  debug: getBooleanEnv('VITE_DEBUG', import.meta.env.DEV)
} as const;

/**
 * Environment info
 */
export const ENV_INFO = {
  mode: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD
} as const;

// Log configuration on startup (only in dev mode)
if (ENV_INFO.isDev) {
  logger.log('🔧 Environment Configuration:', {
    mode: ENV_INFO.mode,
    keycloak: {
      url: KEYCLOAK_CONFIG.url,
      realm: KEYCLOAK_CONFIG.realm,
      clientId: KEYCLOAK_CONFIG.clientId,
      debug: KEYCLOAK_CONFIG.debug
    },
    api: API_CONFIG,
    urls: APP_URLS,
    features: FEATURES
  });
}

// Validate required env vars on startup
export function validateEnvVars(): void {
  const required = [
    'VITE_KEYCLOAK_URL',
    'VITE_KEYCLOAK_REALM',
    'VITE_KEYCLOAK_CLIENT_ID',
    'VITE_KEYCLOAK_ADMIN_URL',
    'VITE_API_BASE_URL',
    'VITE_APP_URL',
    'VITE_PRACTICE_SITE_URL'
  ];

  const missing = required.filter(key => !import.meta.env[key]);

  if (missing.length > 0) {
    const errorMsg = `Missing required environment variables:\n${missing.map(k => `  - ${k}`).join('\n')}\n\nPlease create .env file from env.template`;
    logger.error(errorMsg);
    
    // Only throw in production, show warning in development
    if (ENV_INFO.isProd) {
      throw new Error(errorMsg);
    }
  }
}

// Validate on import (will run when module is loaded)
validateEnvVars();

